import {
  ActiveI9ListFilters,
  SUPPLEMENT_B_PENDING_FILTER,
} from '../../application/domain/entities/ActiveI9ListFilters';
import { IActiveI9Repository } from '../../application/ports/outbound/IActiveI9Repository';
import { ActiveI9Result } from '../../application/domain/entities/ActiveI9Result';
import { SortOrder } from '@/shared/application/domain/enums/SortOrder';
import { HttpClient } from '@/shared/infra/http/HttpClient';
import { LocationItem } from '../../application/domain/entities/LocationItem';
import { LocationResponseDto } from './dto/res/LocationResponseDto';
import { ActiveI9ResponseItemDto } from './dto/res/ActiveI9ResponseItemDto';
import { ActiveI9Mapper } from './mappers/ActiveI9Mapper';

import { I9Status, I9_STATUS_DISPLAY } from '../../application/domain/enums/I9Status';

// Sample status data
export const sampleStatuses = [
  { value: I9Status.ALL_PENDING, label: I9_STATUS_DISPLAY[I9Status.ALL_PENDING] },
  { value: I9Status.SECTION1_PENDING, label: I9_STATUS_DISPLAY[I9Status.SECTION1_PENDING] },
  { value: I9Status.SECTION1_COMPLETED, label: I9_STATUS_DISPLAY[I9Status.SECTION1_COMPLETED] },
  { value: I9Status.SECTION2_COMPLETED, label: I9_STATUS_DISPLAY[I9Status.SECTION2_COMPLETED] },
  // VTK-2017: I-9s with a Supplement B started and not signed, placed after Section 2 Complete at
  // Tyler's request, since a Supplement B follows a completed I-9. Sent as its own flag, not a status.
  { value: SUPPLEMENT_B_PENDING_FILTER, label: 'Supplement B Pending' },
  // Digitized/historical records (VTK-1740) — lets the client filter the I-9 search to historical
  // records. "All Statuses" (the default 'all' option) also includes them.
  { value: I9Status.DIGITIZED, label: I9_STATUS_DISPLAY[I9Status.DIGITIZED] },
  // RQA-03 — filterable so a client can see which historical records have actually been remediated.
  { value: I9Status.REMEDIATED, label: I9_STATUS_DISPLAY[I9Status.REMEDIATED] },
];

export class ApiActiveI9Repository implements IActiveI9Repository {
  constructor(private readonly httpClient: HttpClient) {}

  async getLocations(): Promise<LocationItem[]> {
    try {
      const url = `/i9Form/locations`;
      const response = await this.httpClient.get<LocationResponseDto>(url);

      if (response && Array.isArray(response.data)) {
        return response.data.map(ActiveI9Mapper.toLocationDomain);
      }

      return [];
    } catch (error) {
      console.error('Error fetching locations:', error);
      return [];
    }
  }

  async fetchActiveI9Records(
    page: number,
    pageSize: number,
    location?: string,
    status?: string,
    hireDate?: string,
    employeeFirstName?: string,
    employeeLastName?: string,
    columnName?: string,
    sortOrder?: SortOrder,
    /** Further filters, as an object: the positional list is already nine long. */
    filters?: ActiveI9ListFilters
  ): Promise<ActiveI9Result> {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      if (location) queryParams.append('locationId', location);
      if (status) queryParams.append('status', status);
      if (hireDate) queryParams.append('hireDate', hireDate);
      if (employeeFirstName) queryParams.append('employeeFirstName', employeeFirstName);
      if (employeeLastName) queryParams.append('employeeLastName', employeeLastName);
      if (columnName) queryParams.append('columnName', columnName);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);
      // Filters on the queue's own computed action, so the list cannot disagree with the action each
      // row shows, nor with the dashboard badge that counts them (VTK-1950).
      if (filters?.nextAction) queryParams.append('nextAction', filters.nextAction);
      if (filters?.reverificationReview) queryParams.append('reverificationReview', 'true');
      if (filters?.supplementBPending) queryParams.append('supplementBPending', 'true');

      const url = `/i9Form/all?${queryParams.toString()}`;
      const response = await this.httpClient.getPaginated<ActiveI9ResponseItemDto>(url);

      return {
        items: Array.isArray(response.data) ? response.data.map(ActiveI9Mapper.toDomain) : [],
        totalPages: response.pagination?.totalPages ?? 1,
        totalItems: response.pagination?.totalItems ?? 0,
        currentPage: response.pagination?.currentPage ?? 1,
      };
    } catch (error) {
      console.error('Error fetching active I-9 items:', error);
      throw error;
    }
  }
}
